import { CallActionTypes, CallBaseMessage, CallEndedMessage, CallStartedMessage, CallUpdatedMessage, GetActiveCallsMessage } from '@numeo/types'
import { CallCenterNotification } from 'hooks/useNotification'
import { AppDispatch } from 'store'
import {
    addAiHubActiveCall,
    addApplicationActiveCall,
    endAiHubActiveCall,
    endApplicationActiveCall,
    setApplicationActiveCalls,
    updateAiHubActiveCall,
    updateApplicationActiveCall,
} from 'store/callCenterSlice'
import { upsertNotification } from 'store/notificationSlice'

// AI Hub calls carry `metadata.source = 'agentic-hub'`, stamped in GAS at
// `general-agent-service/src/services/calls/initiate-call.ts:99`. main-service's
// `plivoLivekitCall` writes `metadata: { load: loadData }` with no `source` field, so the
// check is unambiguous today. If you ever add a `source` key to main-service's call
// metadata, update this discriminator.
const isAiHubCall = (metadata: unknown): boolean => {
    return typeof metadata === 'object' && metadata !== null && (metadata as { source?: string }).source === 'agentic-hub'
}

export const handleCallCenterMessage = (message: CallBaseMessage, dispatch: AppDispatch): boolean => {
    let notification: CallCenterNotification | null = new CallCenterNotification(message)

    switch (message.data.action) {
        // Add calls to the state
        case CallActionTypes.CC_GetActiveCalls: {
            const typedData = message.data as GetActiveCallsMessage
            dispatch(setApplicationActiveCalls(typedData.calls))
            notification = null // No notification for GetActiveCalls
            break
        }
        // New call started — route by metadata.source so AI Hub calls land in
        // aiHubActiveCalls (where useAgenticHubCall reads) and DAT/RC stays in the legacy field.
        case CallActionTypes.CC_CallStarted: {
            const typedData = message.data as CallStartedMessage
            if (isAiHubCall(typedData.call.metadata)) {
                dispatch(addAiHubActiveCall(typedData.call))
            } else {
                dispatch(addApplicationActiveCall(typedData.call))
            }
            break
        }
        // Call status updated — same source-based routing as CC_CallStarted.
        case CallActionTypes.CC_CallUpdated: {
            const typedData = message.data as CallUpdatedMessage
            if (isAiHubCall(typedData.call.metadata)) {
                dispatch(updateAiHubActiveCall(typedData.call))
            } else {
                dispatch(updateApplicationActiveCall(typedData.call))
            }
            notification = null
            break
        }
        // Mark call as ended (keep in Redux so transcripts persist). CC_CallEnded only
        // carries callId, no metadata — dispatch to both arrays; whichever holds the call
        // ends it, the other is a no-op (the reducers guard on findIndex !== -1).
        case CallActionTypes.CC_CallEnded: {
            const typedData = message.data as CallEndedMessage
            dispatch(endApplicationActiveCall(typedData.callId))
            dispatch(endAiHubActiveCall(typedData.callId))
            break
        }
        default:
            return false
    }

    if (notification) {
        const plainNotification = {
            id: notification.id,
            category: notification.category,
            message: notification.message,
            secondaryMessage: notification.secondaryMessage,
            timestamp: notification.timestamp,
            type: notification.type,
            path: notification.path,
            topic: notification.topic,
        }

        dispatch(upsertNotification(plainNotification))
    }

    return true
}
